import { Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { resolvePath } from '../utils';
import { useEffect } from 'react';
import PlaceholderInput from './PlaceholderInput';
import { useFieldContext } from './FieldContext';

/**
 * @param {Object} props
 * @param {boolean} props.required
 * @param {string} props.label
 * @param {string} props.errorLabel
 * @param {boolean} props.hideLabel
 * @param {string} props.id
 * @param {string} props.className
 * @param {string} props.placeholder
 * @param {number} props.flex
 * @param {number} props.minWidth
 * @param {number} props.maxWidth
 * @param {string[]} props.options
 * @param {boolean} props.disabled
 * @param {boolean} props.frozen
 */
export default function SelectInput({ required = false, label, errorLabel, hideLabel, id, className = "", placeholder, flex = 1, minWidth = 8, maxWidth, options, disabled, frozen, children, ...rest }) {
    const context = useFieldContext();
    id = context.idPrefix + id;
    disabled ||= context.disabled;

    const { register, unregister, watch, getValues, setValue, formState: { errors, isLoading, disabled: formDisabled }, custom: { noAnimation, noAsterisk } } = useFormContext();
    const error = resolvePath(errors, id);
    disabled ||= formDisabled;

    useEffect(() => () => {
        unregister(id, { keepDefaultValue: true });
    }, []);

    useEffect(() => {
        const value = getValues(id);
        if (value && !options.includes(value)) {
            setValue(id, options[0]);
        }
    }, [options]);

    if (isLoading && !noAnimation) {
        return <PlaceholderInput {...{ required, label, hideLabel, flex, minWidth, maxWidth }}>{children}</PlaceholderInput>;
    }

    return (
        <Form.Group className={`form-group flex flex-col w-full ${className}`} style={{ flex, minWidth: minWidth ? `${minWidth}rem` : undefined, maxWidth: maxWidth ? `${maxWidth}rem` : undefined }}>
            {!hideLabel && <Form.Label className="text-nowrap text-sm font-medium text-neutral-600 mb-2">{label}{required && !noAsterisk && "*"}</Form.Label>}
            <Form.Select className={`py-2 text-lg rounded-sm border-neutral-300 border-b-neutral-200 ${error ? "[&]:border-red-400" : ""} inset-shadow-sm inset-shadow-neutral-200`} disabled={disabled} frozen={frozen ? "" : undefined} isInvalid={error} size="lg" {...rest} {...register(id, { ...rest, required, disabled })}>
                {placeholder && (!required || !watch(id)) && <option value="">{placeholder}</option>}
                {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </Form.Select>
            <Form.Control.Feedback type="invalid" className="mt-0.5 text-sm">No {(errorLabel ?? label).toLowerCase()} selected.</Form.Control.Feedback>
            {children}
        </Form.Group>
    );
}